import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "react-quill/dist/quill.snow.css";
import { useAppDispatch, useAppSelector } from "../../../../hooks/redux-hooks";
import { resetContestByUrl, selectContestsState } from "../../../../store/slices/contests";
import {
  applicationByContest,
  artistsByContest,
  detailByContest,
  favoriteByContest,
} from "../../../../api/asyncThuncks/contestsCards";
import PlaylistByContest from "../playlist/PlaylistByContest";
import Artists from "../../../../components/artists/Artists";
import Button from "../../../../components/button/Button";
import {
  selectMainState,
  setSEOSettings,
} from "../../../../store/slices/main";
import Quill from "../../../../components/common/quill/Quill";
import moment from "moment";
import EmptyContent from "../../../../components/emptyContent/EmptyContent";
import Accordion from "../../../../components/accordion/Accordion";
const loadingGif = require("../../../../assets/loadingGif.gif") as string;

const ContestsById = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { successMessageByVoteModal } =
    useAppSelector(selectMainState);
  const { contestByUrl } = useAppSelector(selectContestsState);
  const { publicUrl } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const startDate = moment(contestByUrl.details?.startedAt).format(
    "DD.MM.YYYY"
  );
  const endDate = moment(contestByUrl.details?.endedAt).format("DD.MM.YYYY");
  const navigateAndSetSEOSettings = (
    link: string,
    appTitle: string,
    appDescription: string,
    appImg: string
  ) => {
    navigate(`${link}`);
    dispatch(
      setSEOSettings({
        appTitle,
        appDescription,
        appImg,
      })
    );
  };

  useEffect(() => {
    dispatch(resetContestByUrl());
    setIsLoading(true);
    dispatch(detailByContest(`${publicUrl}`));
    dispatch(favoriteByContest(`${publicUrl}`));
    dispatch(artistsByContest(`${publicUrl}`))
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [dispatch, publicUrl]);

  useEffect(() => {
    if (publicUrl) dispatch(applicationByContest(`${publicUrl}`));
  }, [dispatch, publicUrl]);

  useEffect(() => {
    if (successMessageByVoteModal) {
      dispatch(artistsByContest(`${publicUrl}`));
    }
  }, [dispatch, successMessageByVoteModal, publicUrl]);

  const stripHtml = (html?: string) => {
    return html ? html.replace(/<[^>]*>/g, "").trim() : "";
  };

  const seoDescription = contestByUrl.details
    ? contestByUrl.details.metaDescription || stripHtml(contestByUrl.details.description).slice(0, 150)
    : "";

  return (
    <>
      {contestByUrl.details && (
        <Helmet>
          <title>{`Радіо «Сільпо» | ${contestByUrl.details.title}`}</title>
          <meta name="title" content={`Радіо «Сільпо» | ${contestByUrl.details.title}`} />
          <meta property="og:title" content={`Радіо «Сільпо» | ${contestByUrl.details.title}`} />
          <meta name="twitter:title" content={`Радіо «Сільпо» | ${contestByUrl.details.title}`} />

          <meta name="description" content={seoDescription} />
          <meta property="og:description" content={seoDescription} />
          <meta name="twitter:description" content={seoDescription} />

          {contestByUrl.details.imgPath && (
            <>
              <meta property="og:image" content={contestByUrl.details.imgPath} />
              <meta name="twitter:image" content={contestByUrl.details.imgPath} />
            </>
          )}
        </Helmet>
      )}
      {!contestByUrl?.details ? (
        <EmptyContent />
      ) : (
        <div className="w-screen max-w-[1000px] bg-white">
          {isLoading ? (
            <div className="w-full flex items-center justify-center pt-36">
              <img
                className="max-w-[50px] max-h-[50px]"
                src={loadingGif}
                alt="loading..."
              />
            </div>
          ) : (
            contestByUrl && (
              <div>
                {contestByUrl?.details && (
                  <div className="w-full px-5 pt-10 pb-10 md:px-0">
                    <div className="w-full flex flex-col items-center md:flex-row">
                      <div className="h-auto flex justify-center w-full">
                        <img
                          className="w-full h-full rounded-lg"
                          src={contestByUrl?.details.imgPath}
                          alt="img"
                        />
                      </div>
                      <div className="w-full md:max-w-[50%] flex-col justify-center items-start flex">
                        <div className="pt-5 md:pl-7 md:pt-0">
                          <h1 className="w-full text-2xl font-bold">
                            {contestByUrl.details.title}
                          </h1>
                          <div className="w-full max-w-[12rem] py-3">
                            <p className="text-[13px] opacity-60">
                              Строки проведення конкурсу
                            </p>
                            <span>
                              {startDate} — {endDate}
                            </span>
                          </div>
                          {/* <div className="w-full max-w-[12rem]">
                            <p className="text-[13px] opacity-60">
                              Заявки приймаються до
                            </p>
                            <span>{applicationButtonEndAt}</span>
                          </div> */}
                          {contestByUrl.details?.ctaButtonUrl && (
                            <Button
                              onClick={() => {
                                window.open(
                                  contestByUrl.details?.ctaButtonUrl,
                                  "_blank"
                                );
                              }}
                              title={
                                contestByUrl.details &&
                                contestByUrl.details.ctaButtonTitle
                              }
                              className="min-w-[120px] w-full mt-10 text-[#202124] text-white bg-orange transition-opacity hover:opacity-90 hover:transition-opacity overflow-hidden text-ellipsis whitespace-nowrap"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <p className="pt-7 text-base">
                      <Quill
                        text={contestByUrl.details.description}
                        className={"contest"}
                      />
                    </p>
                  </div>
                )}

                {contestByUrl.application?.accordions && (
                  <div className="px-5 md:px-0">
                    <h4 className="w-full pt-8 pb-5 text-xl font-bold">
                      Правила участі
                    </h4>
                    <Accordion
                      accordions={contestByUrl.application?.accordions}
                    />
                  </div>
                )}

                {contestByUrl.details?.winningFieldTitle && (
                  <div
                    className="px-5 py-10 rounded-md md:px-10 md:mt-7"
                    style={{
                      background:
                        "linear-gradient(90deg, hsla(251, 100%, 97%, 1) 0%, hsla(231, 100%, 97%, 1) 100%)",
                    }}
                  >
                    <h4 className="w-full pb-6 text-xl font-bold">
                      {contestByUrl.details?.winningFieldTitle}
                    </h4>
                    <Quill
                      text={contestByUrl.details?.winningField}
                      className={"winner"}
                    />
                  </div>
                )}
                <div
                  className={`${
                    !!contestByUrl.favorites?.audioTracks?.length
                      ? "block"
                      : "hidden"
                  } pt-10 px-4 pb-5 md:px-0 md:pb-0`}
                >
                  {
                    <PlaylistByContest
                      title={contestByUrl.favorites?.name}
                      audioTracks={contestByUrl.favorites?.audioTracks}
                      contestId={contestByUrl.details?.id}
                    />
                  }
                </div>
                {contestByUrl.artists && (
                  <div
                    className={`${
                      contestByUrl.artists.length < 0 ? "block" : "hidden"
                    } px-5 md:px-0`}
                  >
                    <Artists
                      title={"Конкурсанти"}
                      artists={contestByUrl.artists}
                    />
                  </div>
                )}

                {/* {!isDatePassed && (
                  <div className="px-5 pt-2 md:px-0">
                    <div
                      onClick={() => {
                        if (contestByUrl.details) {
                          navigateAndSetSEOSettings(
                            `/application/${slug}`,
                            contestByUrl.details.title,
                            contestByUrl.details.description,
                            contestByUrl.details.imgPath
                          );
                        }
                      }}
                    >
                      <Button
                        title="Подати заявку на участь"
                        className="bg-light_gray text-[#202124] md:mt-5 md:bg-light_gray md:bg-opacity-30"
                      />
                    </div>
                  </div>
                )} */}
                {/* <div className="border-b border-light_gray pt-[4rem]"></div> */}
                {/* {contestByUrl.details && (
              <div className="px-5 py-[4rem]">
              <Playlist
              audioTracks={contestByUrl.audio}
              contestId={contestByUrl.details.id}
              />
            </div>
          )} */}
              </div>
            )
          )}
        </div>
      )}
    </>
  );
};

export default ContestsById;
